import { useEffect, useState } from 'react';

import { NoteTemplateFormat } from '@/shared/api/protocol-ts/model/dto_scribe_catalog_pb';
import {
  registryApi,
  type RegistryAsset,
} from '@/shared/api/transcriptor/registryApi';
import {
  templatesApi,
  type CommunityTemplateDetailResponse,
} from '@/shared/api/transcriptor/templatesApi';
import { useAppSelector } from '@/shared/hooks';

import { organizationModel } from '@/entities/organization';
import {
  normalizeDocumentType,
  type DocumentTemplate,
} from '@/entities/templates';

import { adoptRegistryAsset } from '@/features/communityCatalog';

import { LIBRARY_PAGE_SIZE } from '../config';

type LibraryState = {
  assets: RegistryAsset[];
  loading: boolean;
  failed: boolean;
};

// The shared community and official templates the library offers the
// clinic, read as the community catalog reads them: each says whether the
// clinic already has its copy. "Add to my templates" makes that copy.
export const useLibraryTemplates = (enabled: boolean) => {
  const organizationId = useAppSelector(
    organizationModel.selectors.selectCurrentOrganizationID,
  );
  const [state, setState] = useState<LibraryState>({
    assets: [],
    loading: false,
    failed: false,
  });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!enabled || !organizationId) {
      return undefined;
    }
    let active = true;
    setState((current) => ({ ...current, loading: true, failed: false }));
    registryApi
      .listRegistryAssets({
        organizationId,
        family: 'document_template',
        limit: LIBRARY_PAGE_SIZE,
      })
      .then((list) => {
        if (active) {
          setState({ assets: list.items, loading: false, failed: false });
        }
      })
      .catch(() => {
        if (active) {
          setState((current) => ({ ...current, loading: false, failed: true }));
        }
      });
    return () => {
      active = false;
    };
  }, [enabled, organizationId, attempt]);

  return {
    ...state,
    reload: () => setAttempt((current) => current + 1),
    adopt: (asset: RegistryAsset) =>
      adoptRegistryAsset(asset, organizationId ?? ''),
  };
};

const toCommunityTemplate = (
  id: string,
  detail: CommunityTemplateDetailResponse,
): DocumentTemplate => ({
  id,
  series_id: detail.source_series_id || id,
  code: detail.code,
  language: detail.language,
  version: 1,
  status: 'active',
  type: normalizeDocumentType(detail.type),
  name: detail.name,
  tab_title: detail.tab_title,
  description: detail.description ?? null,
  catalog_published: true,
  created_at: detail.published_at ?? '',
  is_system: false,
  sections: detail.sections,
  format: detail.sections.some((section) => section.Lines.length > 0)
    ? NoteTemplateFormat.NoteTemplateFormatBlocks
    : NoteTemplateFormat.NoteTemplateFormatOptions,
});

const readCommunityTemplate = async (id: string, organizationId: string) =>
  toCommunityTemplate(
    id,
    await templatesApi.getCommunityTemplate(id, organizationId),
  );

// A library template read for its preview. A community template is read as
// the community catalog reads it; an official one is the system template
// itself, which any clinic may read.
const readLibraryTemplate = (
  asset: RegistryAsset,
  organizationId: string,
): Promise<DocumentTemplate> =>
  asset.originKind === 'community'
    ? readCommunityTemplate(asset.id, organizationId)
    : templatesApi.getDocumentTemplate(asset.id, organizationId);

const useTemplateReadOnce = (
  read: () => Promise<DocumentTemplate>,
  key: string,
) => {
  const [template, setTemplate] = useState<DocumentTemplate | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    setTemplate(null);
    setFailed(false);
    read()
      .then((loaded) => {
        if (active) {
          setTemplate(loaded);
        }
      })
      .catch(() => {
        if (active) {
          setFailed(true);
        }
      });
    return () => {
      active = false;
    };
    // The caller keys the preview by what it reads, so it is read once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return { template, loading: !template && !failed, failed };
};

export const useLibraryTemplate = (asset: RegistryAsset) => {
  const organizationId = useAppSelector(
    organizationModel.selectors.selectCurrentOrganizationID,
  );
  return useTemplateReadOnce(
    () => readLibraryTemplate(asset, organizationId),
    `${organizationId}:${asset.id}`,
  );
};

export const useCommunityTemplate = (templateId: string) => {
  const organizationId = useAppSelector(
    organizationModel.selectors.selectCurrentOrganizationID,
  );
  return useTemplateReadOnce(
    () => readCommunityTemplate(templateId, organizationId),
    `${organizationId}:${templateId}`,
  );
};
